import React from "react";
import { withTranslation } from "react-i18next";
import { Checkbox, Grid, InputAdornment, Typography } from "@mui/material";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { typeOfOccupancyOptions } from "common/enums";

import { onlyNumberKeys } from "utils";
import {
  fireSafetyBasementCollection,
  fireSafetyFloorCollection,
  fireSafetyGroundFloor,
} from "common/constant";
import { useGetFirePremiseTypeQuery } from "apis/FireSafety";

const BuildingInformation = ({ formik, t, floorOptions }) => {
  const {
    setFieldValue,
    values: {
      fireSafetyAddress: { floors },
    },
  } = formik;

  const { data: { data: fireSafetyPremiseType } = {} } =
    useGetFirePremiseTypeQuery();

  const setNumberOfFloors = (options) => {
    const basementCount = options.filter(({ label }) =>
      fireSafetyBasementCollection.includes(label)
    ).length;

    const groundFloorCount = options.some(({ label }) =>
      fireSafetyGroundFloor.includes(label)
    );
    const floorsCount = options.filter(({ label }) =>
      fireSafetyFloorCollection.includes(label)
    ).length;

    setFieldValue(
      "numberOfFloor",
      `${basementCount ? basementCount + " Basement" : ""}${
        basementCount && groundFloorCount ? " +" : ""
      } ${groundFloorCount ? "G" : ""}${
        (basementCount || groundFloorCount) && floorsCount ? " +" : ""
      } ${floorsCount ? floorsCount + " Floors" : ""}`
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography className="mb-3" lineHeight={1} variant="h6">
          {t("Building Information")}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <SelectField
          label={t("Type of Occupancy") + "*"}
          name="fireSafetyOccupancyTypeId"
          formik={formik}
          options={typeOfOccupancyOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Number of Blocks") + "*"}
          name="numberOfBlock"
          type="number"
          formik={formik}
          placeholder="Enter Number of Blocks"
          InputProps={{
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <SelectField
          label={t("Nature Of Building") + "*"}
          name="fireSafetyPremiseTypeId"
          onChange={(_, { value }) => {
            setFieldValue("fireSafetyPremiseTypeId", value);
          }}
          formik={formik}
          options={fireSafetyPremiseType || []}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Building Height") + "*"}
          name="buildingHeight"
          type="number"
          formik={formik}
          placeholder="Enter Building Height"
          InputProps={{
            endAdornment: <InputAdornment position="end">Mtrs</InputAdornment>,
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Width of Main Entrance") + "*"}
          name="mainEntranceWidth"
          type="number"
          formik={formik}
          placeholder="Enter Width of Main Entrance"
          InputProps={{
            endAdornment: <InputAdornment position="end">Mtrs</InputAdornment>,
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Total Floor Area") + "*"}
          name="totalFloorArea"
          type="number"
          formik={formik}
          placeholder="Enter Total Floor Area"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">Sq. Mtrs</InputAdornment>
            ),
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Build Up Area") + "*"}
          name="buildUpArea"
          type="number"
          formik={formik}
          placeholder="Enter Build Up Area"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">Sq. Mtrs</InputAdornment>
            ),
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} lg={3}>
        <SelectField
          label={t("Floors")}
          name="fireSafetyAddress.floors"
          multiple
          formik={formik}
          disableCloseOnSelect
          value={floors || []}
          options={floorOptions}
          isOptionEqualToValue={(option, { value }) => option.value === value}
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={option.value}>
                <Checkbox sx={{ marginRight: "8px" }} checked={selected} />
                {option.label}
              </li>
            );
          }}
          onChange={(event, options) => {
            if (event.key !== "Backspace" && event.key !== "Delete") {
              setFieldValue("fireSafetyAddress.floors", options);
              setNumberOfFloors(options);
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <InputFieldText
          label={t("Number of Floors")}
          name="numberOfFloor"
          formik={formik}
          placeholder="Enter Number of floors"
          disabled
        />
      </Grid>
    </>
  );
};

BuildingInformation.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
  floorOptions: PropTypes.array,
};

export default withTranslation()(BuildingInformation);
